import { api } from './authService';

export interface AccountNode {
  account: string;
  full_path: string;
  balances: Record<string, number>;
  children: AccountNode[];
}

export interface BalanceResponse {
  accounts: AccountNode[];
  timestamp: string;
}

export interface RegisterEntry {
  date: string;
  payee: string;
  accounts?: { account: string; amount: number; commodity: string }[];
  amount: number;
  commodity: string;
  running_balance: Record<string, number>;
  status: string;
  docs?: string[];
  postings?: { account: string; amount: number; commodity: string }[];
}

export interface RegisterResponse {
  account: string;
  entries: RegisterEntry[];
  total: number;
  has_more: boolean;
  timestamp: string;
}

export async function getBalance(params?: {
  account?: string;
  begin?: string;
  end?: string;
}): Promise<BalanceResponse> {
  const res = await api.get<BalanceResponse>('/balance', { params });
  return res.data;
}

export async function getRegister(params: {
  account: string;
  begin?: string;
  end?: string;
  offset?: number;
  limit?: number;
}): Promise<RegisterResponse> {
  const res = await api.get<RegisterResponse>('/register', { params });
  return res.data;
}

export async function getAccounts(): Promise<{ accounts: string[]; total: number }> {
  const res = await api.get<{ accounts: string[]; total: number }>('/accounts');
  return res.data;
}

export interface MonthlyHistoryEntry {
  year: number;
  month: number;
  label: string;
  receita: number;
  despesas: number;
}

export interface MonthlyHistoryResponse {
  months: MonthlyHistoryEntry[];
  timestamp: string;
}

export async function getMonthlyHistory(params: {
  year: number;
  month: number;
  months?: number;
}): Promise<MonthlyHistoryResponse> {
  const res = await api.get<MonthlyHistoryResponse>('/balance/monthly-history', { params });
  return res.data;
}

// Account management
export async function getDeclaredAccounts(): Promise<{ accounts: string[]; total: number }> {
  const res = await api.get<{ accounts: string[]; total: number }>('/accounts/declared');
  return res.data;
}

export async function createAccount(data: { name: string; commodity?: string }): Promise<{ account: string }> {
  const res = await api.post<{ account: string }>('/accounts', data);
  return res.data;
}

export async function updateAccount(name: string, data: { new_name?: string; commodity?: string }): Promise<{ account: string }> {
  const res = await api.put<{ account: string }>(`/accounts/${encodeURIComponent(name)}`, data);
  return res.data;
}

export async function deleteAccount(name: string): Promise<void> {
  await api.delete(`/accounts/${encodeURIComponent(name)}`);
}

// Transactions
export interface Posting {
  account: string;
  amount: number | null;
  commodity: string | null;
}

export interface Transaction {
  id: string;
  date: string;
  payee: string;
  postings: Posting[];
  tags: Record<string, string>;
  file: string;
  doc_count?: number;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  total: number;
}

export async function getTransactions(params?: {
  begin?: string;
  end?: string;
  account?: string;
  description?: string;
}, signal?: AbortSignal): Promise<TransactionsResponse> {
  const res = await api.get<TransactionsResponse>('/transactions', { params, signal });
  return res.data;
}

export async function createTransaction(data: {
  date: string;
  payee: string;
  postings: Posting[];
  tags?: Record<string, string>;
}): Promise<Transaction> {
  const res = await api.post<Transaction>('/transactions', data);
  return res.data;
}

export async function updateTransaction(id: string, data: {
  date: string;
  payee: string;
  postings: Posting[];
  tags?: Record<string, string>;
}): Promise<Transaction> {
  const res = await api.put<Transaction>(`/transactions/${id}`, data);
  return res.data;
}

export async function deleteTransaction(id: string): Promise<void> {
  await api.delete(`/transactions/${id}`);
}

// Cambio
export interface CambioSummary {
  total_operations: number;
  average_rate: number;
  total_usd: number;
  total_brl: number;
  platforms: Record<string, { count: number; total_brl: number }>;
}

export async function getCambioSummary(): Promise<CambioSummary> {
  const res = await api.get<CambioSummary>('/cambio/summary');
  return res.data;
}

export interface CashflowEntry {
  year: number;
  month: number;
  label: string;
  entradas: number;
  saidas: number;
  entradas_operacional: number;
  saidas_operacional: number;
  entradas_financiamento: number;
  saidas_financiamento: number;
  saldo_acumulado: number;
}

export interface CashflowResponse {
  months: CashflowEntry[];
  timestamp: string;
}

export async function getCashflow(params?: {
  begin?: string;
  end?: string;
}): Promise<CashflowResponse> {
  const res = await api.get<CashflowResponse>('/cashflow', { params });
  return res.data;
}

export interface CashflowTransaction {
  date: string;
  payee: string;
  amount: number;
  contras: string[];
  running_balance: number;
}

export interface CashflowTransactionsResponse {
  transactions: CashflowTransaction[];
  total: number;
}

export async function getCashflowTransactions(params: {
  year: number;
  month: number;
  category: string;
  direction: string;
}): Promise<CashflowTransactionsResponse> {
  const res = await api.get<CashflowTransactionsResponse>('/cashflow/transactions', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Monthly Projection
// ---------------------------------------------------------------------------

export interface ProjectionItem {
  account: string;
  expected_amount: number;
  typical_day: number;
  frequency: number;
  direction: string;
  payee: string;
  realized: boolean;
  realized_amount: number | null;
  realized_transactions: { date: string; amount: number; payee: string }[];
}

export interface ProjectionResponse {
  year: number;
  month: number;
  projections: ProjectionItem[];
  summary: {
    total_expected_entradas: number;
    total_expected_saidas: number;
    total_realized_entradas: number;
    total_realized_saidas: number;
  };
  timestamp: string;
}

export async function getProjection(params: {
  year: number;
  month: number;
}): Promise<ProjectionResponse> {
  const res = await api.get<ProjectionResponse>('/projection', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// DRE (Demonstrativo de Resultado)
// ---------------------------------------------------------------------------

export interface DREEntry {
  year: number;
  month: number;
  label: string;
  receitas: number;
  despesas: number;
  resultado: number;
  resultado_acumulado: number;
}

export interface DREResponse {
  months: DREEntry[];
  timestamp: string;
}

export async function getDRE(params?: {
  begin?: string;
  end?: string;
}): Promise<DREResponse> {
  const res = await api.get<DREResponse>('/dre', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Taxes
// ---------------------------------------------------------------------------

export interface TributoDetail {
  percentual: number;
  valor: number;
}

export interface TaxesResponse {
  regime: string;
  periodo: string;
  rbt12: number | null;
  faturamento: number;
  faixa: number | null;
  aliquota_nominal: number | null;
  deducao: number | null;
  aliquota_efetiva: number;
  valor_das: number | null;
  valor_total?: number | null;
  tributos: Record<string, TributoDetail>;
}

export interface TaxHistoryEntry {
  year: number;
  month: number;
  label: string;
  faturamento: number;
  carga_tributaria: number;
  percentual: number;
}

export interface TaxHistoryResponse {
  months: TaxHistoryEntry[];
}

export async function getTaxes(period: string): Promise<TaxesResponse> {
  const res = await api.get<TaxesResponse>('/taxes', { params: { period } });
  return res.data;
}

export async function getTaxHistory(params: {
  year: number;
  month: number;
  months?: number;
}): Promise<TaxHistoryResponse> {
  const res = await api.get<TaxHistoryResponse>('/taxes/history', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface DREReportResponse {
  periodo: { begin: string; end: string };
  receita_bruta: number;
  impostos_receita: { total: number; detalhamento: string[] };
  receita_liquida: number;
  despesas_operacionais: {
    total: number;
    categorias: { account: string; label: string; value: number }[];
  };
  lucro_operacional: number;
  irpj_csll: number;
  lucro_liquido: number;
  timestamp: string;
}

export async function getDREReport(params: {
  begin: string;
  end: string;
}): Promise<DREReportResponse> {
  const res = await api.get<DREReportResponse>('/reports/dre', { params });
  return res.data;
}

export interface BalanceSheetSection {
  total: number;
  contas: { account: string; label: string; value: number }[];
}

export interface BalanceSheetResponse {
  data: string;
  ativos: BalanceSheetSection;
  passivo: BalanceSheetSection;
  patrimonio_liquido: BalanceSheetSection;
  timestamp: string;
}

export interface TrialBalanceAccount {
  account: string;
  label: string;
  debito: number;
  credito: number;
  saldo: number;
}

export interface TrialBalanceResponse {
  periodo: string;
  contas: TrialBalanceAccount[];
  total_debito: number;
  total_credito: number;
  timestamp: string;
}

export async function getBalanceSheet(params: {
  date: string;
}): Promise<BalanceSheetResponse> {
  const res = await api.get<BalanceSheetResponse>('/reports/balance-sheet', { params });
  return res.data;
}

export async function getTrialBalance(params: {
  period: string;
}): Promise<TrialBalanceResponse> {
  const res = await api.get<TrialBalanceResponse>('/reports/trial-balance', { params });
  return res.data;
}

// ---------------------------------------------------------------------------
// Import
// ---------------------------------------------------------------------------

export interface ParsedTransaction {
  date: string;
  description: string;
  amount: number;
}

export interface ParseResponse {
  transactions: ParsedTransaction[];
  total: number;
}

export async function parseImportFile(
  file: File,
  options: {
    format: string;
    separator?: string;
    date_col?: number;
    description_col?: number;
    amount_col?: number;
    date_format?: string;
    skip_header?: boolean;
    source_account?: string;
  },
): Promise<ParseResponse> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('format', options.format);
  if (options.separator) formData.append('separator', options.separator);
  if (options.date_col !== undefined) formData.append('date_col', String(options.date_col));
  if (options.description_col !== undefined) formData.append('description_col', String(options.description_col));
  if (options.amount_col !== undefined) formData.append('amount_col', String(options.amount_col));
  if (options.date_format) formData.append('date_format', options.date_format);
  if (options.skip_header !== undefined) formData.append('skip_header', String(options.skip_header));
  if (options.source_account) formData.append('source_account', options.source_account);
  const res = await api.post<ParseResponse>('/transactions/import/parse', formData);
  return res.data;
}

export interface ImportTransaction {
  date: string;
  description: string;
  amount: number;
  category: string;
  source_account: string;
}

export interface CommitResponse {
  created: number;
  errors: { index: number; error: string }[];
  transactions: unknown[];
}

export async function commitImport(transactions: ImportTransaction[]): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit', { transactions });
  return res.data;
}

export interface DASTributo {
  codigo: string;
  nome: string;
  account: string;
  valor: number;
}

export interface DASData {
  periodo: string;
  data_vencimento: string;
  valor_total: number;
  tributos: DASTributo[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface CDBData {
  produto: string;
  data_aplicacao: string;
  data_resgate: string;
  valor_aplicado: number;
  rendimento_bruto: number;
  iof: number;
  ir: number;
  valor_resgate_liquido: number;
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export async function commitCDBImport(data: {
  date: string;
  description: string;
  postings: { account: string; amount: number; commodity: string }[];
}): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit-cdb', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// CEF Loan Import
// ---------------------------------------------------------------------------

export interface CEFLoanCommitResponse {
  updated: number;
  total_parcelas: number;
  results: { parcela: number; replaced: string }[];
  errors: { parcela: number; error: string }[];
  loan_account: string;
}

export async function commitCEFLoanImport(data: {
  contrato_numero: string;
  contrato_produto: string;
  parcelas: {
    numero: number;
    vencimento: string;
    amortizacao: number;
    juros: number;
    encargos: number;
    valor_total_pago: number;
    saldo_devedor: number;
    status: string;
  }[];
}): Promise<CEFLoanCommitResponse> {
  const res = await api.post<CEFLoanCommitResponse>('/transactions/import/commit-cef-loan', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Payroll Import
// ---------------------------------------------------------------------------

export interface PayrollFuncionario {
  nome: string;
  tipo: string;
  vencimentos: number;
  descontos: number;
  liquido: number;
  inss: number;
  irrf: number;
  fgts: number;
}

export interface PayrollData {
  competencia: string;
  funcionarios: PayrollFuncionario[];
  transactions: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  }[];
}

export interface FGTSFuncionario {
  nome: string;
  matricula: string;
  remuneracao: number;
  fgts: number;
}

export interface FGTSRelatorioData {
  competencia: string;
  total_fgts: number;
  funcionarios: FGTSFuncionario[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface DARFTributo {
  codigo: string;
  nome: string;
  valor: number;
  account_despesa: string;
  account_passivo: string;
}

export interface DARFFederalData {
  competencia: string;
  valor_total: number;
  tributos: DARFTributo[];
  transaction: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  };
}

export interface FGTSGuiaData {
  competencia: string;
  valor_total: number;
  data_vencimento: string;
}

export interface LiquidosFuncionario {
  nome: string;
  codigo: string;
  valor: number;
}

export interface LiquidosData {
  competencia: string;
  funcionarios: LiquidosFuncionario[];
  total: number;
}

export interface PayrollCommitRequest {
  competencia: string;
  transactions: {
    date: string;
    description: string;
    postings: { account: string; amount: number; commodity: string }[];
  }[];
  funcionarios?: PayrollFuncionario[];
}

export async function commitPayrollImport(data: PayrollCommitRequest): Promise<CommitResponse> {
  const res = await api.post<CommitResponse>('/transactions/import/commit-payroll', data);
  return res.data;
}

export interface CrossValidateRequest {
  holerite?: PayrollData | null;
  fgts_relatorio?: FGTSRelatorioData | null;
  liquidos?: LiquidosData | null;
  fgts_guia?: FGTSGuiaData | null;
}

export async function validatePayroll(data: CrossValidateRequest): Promise<{ warnings: string[] }> {
  const res = await api.post<{ warnings: string[] }>('/transactions/import/validate-payroll', data);
  return res.data;
}

// ---------------------------------------------------------------------------
// Reconciliation
// ---------------------------------------------------------------------------

export interface ReconciliationSummary {
  account: string;
  cleared_balance: number;
  cleared_count: number;
  pending_balance: number;
  pending_count: number;
  uncleared_balance: number;
  uncleared_count: number;
  expected_balance: number;
}

export interface ReconciliationPosting {
  date: string;
  payee: string;
  amount: number;
  commodity: string;
  status: 'uncleared' | 'pending' | 'cleared';
  file: string;
  line: number;
  contra_accounts: { account: string; amount: number; commodity: string }[];
}

export interface ReconciliationTransactionsResponse {
  account: string;
  postings: ReconciliationPosting[];
  total: number;
  limit: number;
  offset: number;
}

export async function getReconciliationSummary(account: string): Promise<ReconciliationSummary> {
  const res = await api.get<ReconciliationSummary>('/reconciliation/summary', { params: { account } });
  return res.data;
}

export async function getReconciliationTransactions(params: {
  account: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<ReconciliationTransactionsResponse> {
  const res = await api.get<ReconciliationTransactionsResponse>('/reconciliation/transactions', { params });
  return res.data;
}

export async function markPostings(postings: { file: string; line: number; new_status: string }[]): Promise<{ changed: number }> {
  const res = await api.post<{ changed: number }>('/reconciliation/mark', { postings });
  return res.data;
}

export interface ReconcileResult {
  success: boolean;
  changed: number;
  cleared_balance: number;
  statement_balance: number;
  difference: number;
}

export async function reconcileBatch(data: {
  account: string;
  statement_balance: number;
  posting_ids: { file: string; line: number }[];
}): Promise<ReconcileResult> {
  const res = await api.post<ReconcileResult>('/reconciliation/reconcile', data);
  return res.data;
}

export async function downloadReportPDF(
  reportType: 'dre' | 'balance-sheet' | 'trial-balance',
  params: Record<string, string>,
): Promise<void> {
  const res = await api.get(`/reports/${reportType}/pdf`, {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename=(.+)/);
  link.download = match ? match[1] : `${reportType}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Domínio Sistemas export
// ---------------------------------------------------------------------------

export interface DominioMappingStatus {
  mapped: { account: string; dominio_code: string }[];
  unmapped: string[];
  total_accounts: number;
  total_mapped: number;
}

export interface DominioExportPreview {
  exported: number;
  skipped: number;
  total: number;
  unmapped_accounts: string[];
}

export async function getDominioMapping(): Promise<DominioMappingStatus> {
  const res = await api.get('/reports/dominio/mapping');
  return res.data;
}

export async function getDominioExportPreview(params: { begin: string; end: string }): Promise<DominioExportPreview> {
  const res = await api.get('/reports/dominio/export', { params });
  return res.data;
}

export async function downloadDominioExport(params: { begin: string; end: string }): Promise<void> {
  const res = await api.get('/reports/dominio/download', {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?(.+?)"?$/);
  link.download = match ? match[1] : `dominio_${params.begin}_${params.end}.txt`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export async function downloadRegisterPDF(
  account: string,
  begin?: string,
  end?: string,
): Promise<void> {
  const params: Record<string, string> = { account };
  if (begin) params.begin = begin;
  if (end) params.end = end;
  const res = await api.get('/register/pdf', { params, responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  const disposition = res.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?(.+?)"?$/);
  link.download = match ? match[1] : `extrato_${account}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
